// Single source of truth for fuel value rounding/formatting on the client.
// Mirrors `cockpit-it-server/utils/fuel-math.js` so the same math runs on both
// sides. Server stores aggregates in lb (INT). For KG operators we display
// whole kilograms; for lb operators we keep two decimals.

export const KG_PER_LB = 0.45359237;
export const LB_PER_KG = 1 / KG_PER_LB;
export const LITERS_PER_US_GALLON = 3.785411784;

export type MassDisplayUnit = "kg" | "lb";

export const normalizeMassUnit = (unit?: string | null): MassDisplayUnit => {
  const normalized = `${unit || ""}`.trim().toLowerCase();
  return normalized === "kg" ? "kg" : "lb";
};

const toFiniteNumber = (
  value: number | string | null | undefined,
): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const roundFuelWeight = (
  value: number | string | null | undefined,
  unit: MassDisplayUnit | string = "lb",
): number | null => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return null;
  }
  const digits = normalizeMassUnit(unit) === "kg" ? 0 : 2;
  const factor = 10 ** digits;
  return Math.round(numeric * factor) / factor;
};

// Returns a value suitable for direct display in a table cell. When the
// underlying value is not numeric we preserve it as-is so existing
// placeholder strings ("N/A", "") keep working.
export const formatFuelWeightCell = (
  value: number | string | null | undefined,
  unit: MassDisplayUnit | string = "lb",
): number | string => {
  const rounded = roundFuelWeight(value, unit);
  if (rounded === null) {
    return value ?? "";
  }
  return rounded;
};

// Numeric-only variant for arithmetic (sums of fuel weights). Falls back to
// 0 to keep call-sites compact.
export const fuelWeightForCalc = (
  value: number | string | null | undefined,
  unit: MassDisplayUnit | string = "lb",
): number => roundFuelWeight(value, unit) ?? 0;

const DECIMAL_DIGITS = 2;

export const roundNumber = (
  value: number | string | null | undefined,
  digits: number = DECIMAL_DIGITS,
): number | null => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return null;
  }
  const factor = 10 ** digits;
  return Math.round(numeric * factor) / factor;
};

export const formatNumberInput = (
  value: number | null,
  digits: number = DECIMAL_DIGITS,
): string => {
  if (value === null || !Number.isFinite(value)) return "";
  const rounded = roundNumber(value, digits);
  return rounded === null ? "" : `${rounded}`;
};
